import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Globe, Plus, Trash2, Shield } from "lucide-react";
import { format } from "date-fns";

interface IpEntry {
  id: string;
  client_id: string;
  ip_address: string;
  label: string | null;
  created_by: string | null;
  created_at: string;
}

// Simple IPv4/IPv6 validation
function isValidIp(ip: string): boolean {
  const trimmed = ip.trim();
  // IPv4
  const ipv4 = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (ipv4.test(trimmed)) {
    return trimmed.split(".").every((octet) => {
      const n = parseInt(octet, 10);
      return n >= 0 && n <= 255;
    });
  }
  // IPv4 CIDR
  const ipv4Cidr = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;
  if (ipv4Cidr.test(trimmed)) {
    const [addr, prefix] = trimmed.split("/");
    const validAddr = addr.split(".").every((octet) => {
      const n = parseInt(octet, 10);
      return n >= 0 && n <= 255;
    });
    const prefixNum = parseInt(prefix, 10);
    return validAddr && prefixNum >= 0 && prefixNum <= 32;
  }
  // IPv6 (simplified check)
  const ipv6 = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/;
  if (ipv6.test(trimmed)) return true;
  // IPv6 CIDR
  const ipv6Cidr = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}\/\d{1,3}$/;
  if (ipv6Cidr.test(trimmed)) return true;
  return false;
}

const IpWhitelist = () => {
  const { profile, clientInfo, user } = useAuth();
  const { toast } = useToast();

  const [allowAllIps, setAllowAllIps] = useState(true);
  const [entries, setEntries] = useState<IpEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Add form
  const [newIp, setNewIp] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<IpEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Load allow_all_ips flag from client
  useEffect(() => {
    if (!clientInfo) return;
    const loadFlag = async () => {
      const { data } = await supabase
        .from("client")
        .select("allow_all_ips")
        .eq("id", clientInfo.id)
        .single();
      setAllowAllIps(data?.allow_all_ips ?? true);
    };
    loadFlag();
  }, [clientInfo]);

  // Load whitelist entries
  useEffect(() => {
    if (!profile?.client_id) return;
    const loadEntries = async () => {
      const { data, error } = await (supabase
        .from("ip_whitelist" as any)
        .select("id, client_id, ip_address, label, created_by, created_at")
        .eq("client_id", profile.client_id)
        .order("created_at", { ascending: false }) as any);

      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        setEntries((data as IpEntry[]) ?? []);
      }
      setLoading(false);
    };
    loadEntries();
  }, [profile?.client_id]);

  // Audit log helper
  const logAudit = async (action: string, resourceName: string, metadata: Record<string, unknown> = {}) => {
    if (!profile?.client_id || !user) return;
    await (supabase
      .from("audit_logs" as any)
      .insert({
        client_id: profile.client_id,
        user_id: user.id,
        user_email: user.email ?? null,
        user_name: profile.full_name ?? null,
        action,
        resource_type: "ip_whitelist",
        resource_name: resourceName,
        metadata,
      } as any) as any);
  };

  // Toggle allow_all_ips flag
  const handleToggleAllowAll = async (value: boolean) => {
    if (!clientInfo || saving) return;
    setSaving(true);
    const { error } = await supabase
      .from("client")
      .update({ allow_all_ips: value } as any)
      .eq("id", clientInfo.id);
    setSaving(false);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setAllowAllIps(value);
      await logAudit("edited", "Allow All IPs", {
        field: "allow_all_ips",
        old_value: !value,
        new_value: value,
      });
      toast({
        title: value ? "Access from all IPs enabled" : "IP restriction enabled",
        description: value
          ? "The portal is now accessible from any IP address."
          : "Only whitelisted IP addresses can access the portal.",
      });
    }
  };

  // Add new IP
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedIp = newIp.trim();
    if (!trimmedIp) return;

    if (!isValidIp(trimmedIp)) {
      toast({
        title: "Invalid IP address",
        description: "Please enter a valid IPv4 or IPv6 address (CIDR notation supported).",
        variant: "destructive",
      });
      return;
    }

    // Check for duplicates
    if (entries.some((e) => e.ip_address === trimmedIp)) {
      toast({
        title: "Duplicate IP",
        description: "This IP address is already in the whitelist.",
        variant: "destructive",
      });
      return;
    }

    setAdding(true);
    const { data, error } = await (supabase
      .from("ip_whitelist" as any)
      .insert({
        client_id: profile!.client_id,
        ip_address: trimmedIp,
        label: newLabel.trim() || null,
        created_by: user!.id,
      } as any)
      .select("id, client_id, ip_address, label, created_by, created_at")
      .single() as any);

    setAdding(false);

    if (error) {
      if (error.code === "23505") {
        toast({ title: "Duplicate IP", description: "This IP address is already whitelisted.", variant: "destructive" });
      } else {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      }
    } else if (data) {
      setEntries((prev) => [data as IpEntry, ...prev]);
      setNewIp("");
      setNewLabel("");
      await logAudit("created", trimmedIp, {
        ip_address: trimmedIp,
        label: newLabel.trim() || null,
      });
      toast({ title: "IP added", description: `${trimmedIp} has been added to the whitelist.` });
    }
  };

  // Delete IP
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);

    const { error } = await (supabase
      .from("ip_whitelist" as any)
      .delete()
      .eq("id", deleteTarget.id) as any);

    setDeleting(false);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setEntries((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      await logAudit("deleted", deleteTarget.ip_address, {
        ip_address: deleteTarget.ip_address,
        label: deleteTarget.label,
      });
      toast({ title: "IP removed", description: `${deleteTarget.ip_address} has been removed from the whitelist.` });
    }
    setDeleteTarget(null);
  };

  if (!profile?.client_id) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
        <Globe className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">No client assigned to your account.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 md:p-6 overflow-y-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Shield className="h-6 w-6" /> IP Whitelist
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Control which IP addresses can access the portal. When IP restriction is enabled,
          only users connecting from whitelisted IP addresses can log in and use the platform.
        </p>
      </div>

      {/* Allow All IPs Toggle */}
      <div className="flex items-center gap-4 rounded-lg border border-border bg-card p-4 max-w-lg">
        <div className="flex-1">
          <Label htmlFor="allow-all-ips" className="text-base font-medium">
            Allow access from all IP addresses
          </Label>
          <p className="text-sm text-muted-foreground mt-0.5">
            {allowAllIps
              ? "The portal is accessible from any IP address."
              : "Only whitelisted IP addresses can access the portal."}
          </p>
        </div>
        {saving ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <Switch
            id="allow-all-ips"
            checked={allowAllIps}
            onCheckedChange={handleToggleAllowAll}
          />
        )}
      </div>

      {/* IP whitelist management - shown when allow_all_ips is disabled */}
      {!allowAllIps && (
        <div className="space-y-4 max-w-4xl">
          {/* Add IP form */}
          <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-ip">IP Address</Label>
              <Input
                id="new-ip"
                type="text"
                placeholder="e.g. 192.168.1.0/24"
                value={newIp}
                onChange={(e) => setNewIp(e.target.value)}
                className="w-[220px]"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-label">Label (optional)</Label>
              <Input
                id="new-label"
                type="text"
                placeholder="e.g. Office Network"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                className="w-[200px]"
              />
            </div>
            <Button type="submit" disabled={adding || !newIp.trim()} className="gap-2">
              {adding ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Add IP
            </Button>
          </form>

          {/* IP list */}
          {entries.length === 0 ? (
            <Card className="flex flex-col items-center justify-center py-12">
              <Globe className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-1">No whitelisted IPs</p>
              <p className="text-sm text-muted-foreground text-center max-w-md">
                Add IP addresses above to allow access from specific locations.
                Without any whitelisted IPs and restriction enabled, no one will be able to access the portal.
              </p>
            </Card>
          ) : (
            <Card className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Added On</TableHead>
                    <TableHead className="w-[60px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <Badge variant="secondary" className="font-mono text-sm">
                          {entry.ip_address}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {entry.label || "—"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {format(new Date(entry.created_at), "MMM d, yyyy HH:mm")}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setDeleteTarget(entry)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}

          <p className="text-xs text-muted-foreground">
            Supports IPv4, IPv6, and CIDR notation (e.g. 10.0.0.0/8). Changes take effect immediately for new login attempts.
          </p>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove IP address?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <span className="font-mono font-medium">{deleteTarget?.ip_address}</span>
              {deleteTarget?.label && <> ({deleteTarget.label})</>} from the whitelist.
              Users connecting from this IP will no longer be able to access the portal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default IpWhitelist;
